import React, { useState, useEffect } from 'react';
import {
  getAllTopicsExercisesStatus,
  fetchDynamicLessons,
  generateDynamicLesson,
  deleteDynamicLesson,
  fetchHiddenLessons,
  fetchToday,
  STATIC_BASE_URL,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { computeLevel, LEVEL_COLORS } from './shared/XPBadge';
import { BUILT_IN_LESSONS } from '../data/builtInLessons';

// Převod backendového exercise_type na frontend id cvičení (opak exerciseTypeMap
// v ExerciseMenu) a český název pro sekci „Co dnes".
const EXERCISE_TYPE_TO_FRONTEND_ID = {
  conversation_writing: 'conversation',
  conversation_fillin: 'conversation-fillin',
  vocabulary_fillin: 'vocabulary',
  speaking: 'speaking',
  writing: 'writing',
  ai_conversation: 'ai-conversation',
  conversation_listening: 'listening',
};

const EXERCISE_TYPE_LABEL = {
  conversation_writing: 'Dialogy',
  conversation_fillin: 'Doplňování',
  vocabulary_fillin: 'Slovíčka',
  speaking: 'Mluvení',
  writing: 'Psaní',
  ai_conversation: 'Konverzace s AI',
  conversation_listening: 'Poslech',
};

// Název lekce podle topic; pro neznámé (dynamické) téma vrátí samotný topic.
const TOPIC_TITLE = Object.fromEntries(BUILT_IN_LESSONS.map((l) => [l.topic, l.title]));
const topicTitle = (topic) => TOPIC_TITLE[topic] || topic;

// Dialogové typy potřebují konkrétní dialog, který z conversation_id na frontendu
// spolehlivě nezmapujeme → otevřeme rozcestník lekce, kde uživatel dialog vybere.
const isDialogScoped = (exerciseType) =>
  exerciseType === 'conversation_writing' || exerciseType === 'conversation_listening';

const UserLevelBadge = ({ user }) => {
  if (user?.total_xp == null) return null;
  const [level, levelName] = computeLevel(user.total_xp);
  const levelColor = LEVEL_COLORS[level] || LEVEL_COLORS[1];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${levelColor}`}>
      {levelName} · {user.total_xp} XP
    </span>
  );
};

const LessonMenu = ({ onSelectLesson, onOpenDashboard, onOpenAdmin }) => {
  const { user, logout, refreshUser } = useAuth();

  useEffect(() => {
    refreshUser();
  }, []);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dynamicLessons, setDynamicLessons] = useState([]);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [topicInput, setTopicInput] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // {conversationId, topic} | null
  const [hiddenTopics, setHiddenTopics] = useState([]);
  const [today, setToday] = useState(null); // data pro sekci „Co dnes"

  // Lekce schované adminem se v menu nezobrazují. Filtrujeme pevný katalog;
  // sekvenční odemykání (isLessonLocked) pak počítá jen přes viditelné lekce.
  const lessons = BUILT_IN_LESSONS.filter((l) => !hiddenTopics.includes(l.topic));

  // Fetch progress data on mount
  useEffect(() => {
    const fetchProgress = async () => {
      try {
        setLoading(true);
        const data = await getAllTopicsExercisesStatus();
        setProgressData(data.topics);
      } catch (error) {
        console.error('Error fetching progress:', error);
        // Continue without progress data
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, []);

  // Načti seznam schovaných lekcí, ať je odfiltrujeme z menu.
  useEffect(() => {
    const loadHidden = async () => {
      try {
        const data = await fetchHiddenLessons();
        setHiddenTopics(data.hidden_topics || []);
      } catch (error) {
        console.error('Error fetching hidden lessons:', error);
      }
    };
    loadHidden();
  }, []);

  // Načti data pro sekci „Co dnes" (co dnes zopakovat + kde naposledy skončil).
  useEffect(() => {
    const loadToday = async () => {
      try {
        const data = await fetchToday();
        setToday(data);
      } catch (error) {
        console.error('Error fetching today review:', error);
      }
    };
    loadToday();
  }, []);

  // Fetch user's dynamic lessons on mount
  useEffect(() => {
    const loadDynamic = async () => {
      try {
        const data = await fetchDynamicLessons();
        setDynamicLessons(data.lessons || []);
      } catch (error) {
        console.error('Error fetching dynamic lessons:', error);
      }
    };
    loadDynamic();
  }, []);

  const openTopicModal = () => {
    setTopicInput('');
    setGenerateError(null);
    setShowTopicModal(true);
  };

  const closeTopicModal = () => {
    if (generating) return;
    setShowTopicModal(false);
    setTopicInput('');
    setGenerateError(null);
  };

  const handleGenerate = async () => {
    const topic = topicInput.trim();
    if (!topic) {
      setGenerateError('Napiš téma lekce.');
      return;
    }
    setGenerating(true);
    setGenerateError(null);
    try {
      const created = await generateDynamicLesson(topic);
      setDynamicLessons((prev) => [created, ...prev]);
      setShowTopicModal(false);
      setTopicInput('');
    } catch (error) {
      console.error('Error generating dynamic lesson:', error);
      setGenerateError(error.message || 'Generování se nepodařilo. Zkus to prosím znovu.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteDynamic = async () => {
    if (!confirmDelete) return;
    const { conversationId } = confirmDelete;
    setDeletingId(conversationId);
    try {
      await deleteDynamicLesson(conversationId);
      setDynamicLessons((prev) => prev.filter((l) => l.conversation_id !== conversationId));
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error deleting dynamic lesson:', error);
      alert(error.message || 'Smazání se nepodařilo.');
    } finally {
      setDeletingId(null);
    }
  };

  const openDynamicLesson = (lesson) => {
    // Don't set exerciseType — let ExerciseMenu render with conversation-scoped filtering.
    onSelectLesson(lesson.topic, {
      dialogueNumber: lesson.dialogue_number,
      conversationId: lesson.conversation_id,
    });
  };

  // Otevře cvičení ze sekce „Co dnes". Topic-scoped typy otevřeme rovnou;
  // dialogové (Dialogy/Poslech) přes rozcestník lekce, ať uživatel vybere dialog.
  const openTodayItem = (item) => {
    if (!item) return;
    const frontendId = EXERCISE_TYPE_TO_FRONTEND_ID[item.exercise_type];
    if (!frontendId || isDialogScoped(item.exercise_type)) {
      onSelectLesson(item.topic);
    } else {
      onSelectLesson(item.topic, { exerciseType: frontendId });
    }
  };

  // Helper function to render lesson completion status
  const renderLessonStatus = (topic) => {
    if (!progressData || !progressData[topic]) {
      return (
        <div style={{
          marginTop: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: '#999',
          fontWeight: '500',
          fontSize: '0.9rem'
        }}>
          ○ Nedokončeno
        </div>
      );
    }

    const topicProgress = progressData[topic];
    const writingExercise = topicProgress.exercises.find(ex => ex.exercise_type === 'writing');
    const isLessonCompleted = writingExercise?.finished === true;

    return (
      <div style={{
        marginTop: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        color: isLessonCompleted ? '#28a745' : '#999',
        fontWeight: '500',
        fontSize: '0.9rem'
      }}>
        {isLessonCompleted ? '✓ Dokončeno' : '○ Nedokončeno'}
      </div>
    );
  };

  const isLessonLocked = (index) => {
    if (index === 0) return false; // First lesson always unlocked

    // If we don't have progress data yet, assume locked to be safe
    if (!progressData) return true;

    const previousLesson = lessons[index - 1];
    const previousTopicProgress = progressData[previousLesson.topic];

    // If no data for previous lesson, it's not started/finished -> locked
    if (!previousTopicProgress) return true;

    // Check if writing exercise in previous lesson is finished
    const writingExercise = previousTopicProgress.exercises.find(ex => ex.exercise_type === 'writing');
    return !writingExercise?.finished;
  };

  return (
    <div style={{
      backgroundColor: '#f5f5f5',
      minHeight: '100vh',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '1000px',
        margin: '0 auto',
        textAlign: 'center'
      }}>
        {/* Navigační tlačítka */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={onOpenDashboard}
              style={{
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '14px 24px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#e0e0e0';
              }}
            >
              <span style={{ fontSize: '1rem', fontWeight: '500', color: '#1a1a1a' }}>Můj pokrok</span>
            </button>
            {user && user.is_admin && (
              <button
                onClick={onOpenAdmin}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '14px 24px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.borderColor = '#ccc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }}
              >
                <span style={{ fontSize: '1rem', fontWeight: '500', color: '#1a1a1a' }}>Admin</span>
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <UserLevelBadge user={user} />
            <span style={{ fontSize: '0.95rem', fontWeight: '500', color: '#666' }}>
              {user?.username}
            </span>
            <button
              onClick={logout}
              style={{
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '14px 20px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#e0e0e0';
              }}
            >
              <span style={{ fontSize: '1rem', fontWeight: '500', color: '#1a1a1a' }}>Odhlásit</span>
            </button>
          </div>
        </div>

        <h1 style={{
          fontSize: '2rem',
          fontWeight: '500',
          marginBottom: '8px',
          color: '#1a1a1a'
        }}>
          English Teacher
        </h1>

        <p style={{
          fontSize: '1rem',
          color: '#666',
          marginBottom: '40px'
        }}>
          Vyberte lekci
        </p>

        {/* Sekce „Co dnes": kolik dnes zopakovat + kde student naposledy skončil. */}
        {today && (
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '32px',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>
              Co dnes
            </h2>

            {today.review_count > 0 ? (
              <p style={{ fontSize: '1.1rem', color: '#333', margin: 0 }}>
                Máš <strong>{today.review_count}</strong> cvičení k zopakování.
              </p>
            ) : (
              <p style={{ fontSize: '1.1rem', color: '#333', margin: 0 }}>
                Dnes nic k opakování — můžeš klidně pokračovat v lekcích.
              </p>
            )}

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {today.review_count > 0 && (
                <button
                  onClick={() => openTodayItem(today.review_items[0])}
                  style={{
                    backgroundColor: '#0d6efd',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '14px 24px',
                    fontSize: '1.05rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Dnešní opakování
                </button>
              )}
              {today.continue_item && (
                <button
                  onClick={() => openTodayItem(today.continue_item)}
                  style={{
                    backgroundColor: 'white',
                    color: '#1a1a1a',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    padding: '14px 24px',
                    fontSize: '1.05rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Pokračovat: {topicTitle(today.continue_item.topic)}
                </button>
              )}
            </div>

            {today.review_count > 0 && (
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#555', fontSize: '0.98rem' }}>
                {today.review_items.map((item, i) => (
                  <li key={i} style={{ marginBottom: '4px' }}>
                    <button
                      onClick={() => openTodayItem(item)}
                      style={{
                        background: 'none', border: 'none', padding: 0,
                        color: '#0d6efd', cursor: 'pointer', fontSize: '0.98rem',
                        textDecoration: 'underline', textAlign: 'left'
                      }}
                    >
                      {topicTitle(item.topic)} — {EXERCISE_TYPE_LABEL[item.exercise_type] || item.exercise_type}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px'
        }}>
          {lessons.map((lesson, index) => {
            const locked = isLessonLocked(index);

            return (
              <button
                key={lesson.id}
                onClick={() => !locked && onSelectLesson(lesson.topic)}
                disabled={locked}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '0',
                  cursor: locked ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: locked ? 0.7 : 1,
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!locked) {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.borderColor = '#ccc';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!locked) {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }
                }}
              >
                {locked && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}>
                    🔒
                  </div>
                )}
                <div style={{
                  width: '100%',
                  height: '160px',
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  filter: locked ? 'grayscale(100%)' : 'none'
                }}>
                  <img
                    src={lesson.image}
                    alt={lesson.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div style="color: #999; font-size: 0.9rem;">Image</div>';
                    }}
                  />
                </div>
                <div style={{
                  padding: '20px'
                }}>
                  <h3 style={{
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    marginBottom: '8px',
                    color: '#1a1a1a'
                  }}>
                    {lesson.title}
                  </h3>
                  <p style={{
                    fontSize: '0.9rem',
                    color: '#666',
                    margin: '0',
                    lineHeight: '1.4'
                  }}>
                    {lesson.description}
                  </p>

                  {/* Lesson completion status */}
                  {renderLessonStatus(lesson.topic)}

                  <div style={{
                    fontSize: '0.8rem',
                    color: lesson.color, // Note: lesson.color is undefined in the lessons array above, but keeping it as is from original code
                    fontWeight: '500',
                    marginTop: '10px'
                  }}>
                    Lekce {index + 1}
                  </div>
                </div>
              </button>
            );
          })}

          {/* Dynamic lessons owned by the current user */}
          {dynamicLessons.map((lesson) => (
            <div
              key={`dyn-${lesson.conversation_id}`}
              style={{
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '0',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'left',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
              }}
              onClick={() => openDynamicLesson(lesson)}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = '#ccc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#e0e0e0';
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDelete({ conversationId: lesson.conversation_id, topic: lesson.topic });
                }}
                title="Smazat lekci"
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 10,
                  fontSize: '16px',
                  lineHeight: 1
                }}
              >
                ×
              </button>
              <div style={{
                position: 'relative',
                width: '100%',
                height: '160px',
                background: 'linear-gradient(135deg, #e0f2fe 0%, #ede9fe 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '4rem',
                color: '#6366f1',
                overflow: 'hidden'
              }}>
                ✨
                {lesson.image_url && (
                  <img
                    src={`${STATIC_BASE_URL}${lesson.image_url}`}
                    alt={lesson.topic}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>
              <div style={{ padding: '20px' }}>
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: '500',
                  marginBottom: '8px',
                  color: '#1a1a1a'
                }}>
                  {lesson.topic}
                </h3>
                <p style={{
                  fontSize: '0.9rem',
                  color: '#666',
                  margin: '0',
                  lineHeight: '1.4'
                }}>
                  Tvoje dynamická lekce — dialog na míru
                </p>
                <div style={{
                  fontSize: '0.8rem',
                  color: '#6366f1',
                  fontWeight: '500',
                  marginTop: '10px'
                }}>
                  Dynamická lekce
                </div>
              </div>
            </div>
          ))}

          {/* 11th tile — "+" to create a new dynamic lesson */}
          <button
            onClick={openTopicModal}
            style={{
              backgroundColor: 'transparent',
              border: '2px dashed #cbd5e1',
              borderRadius: '8px',
              padding: '0',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              textAlign: 'left',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: '280px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#6366f1';
              e.currentTarget.style.backgroundColor = '#f5f3ff';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#cbd5e1';
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              gap: '12px'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#eef2ff',
                color: '#6366f1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2.5rem',
                fontWeight: '300',
                lineHeight: 1
              }}>
                +
              </div>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '500',
                color: '#1a1a1a',
                margin: 0
              }}>
                Dynamická lekce
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: '#666',
                textAlign: 'center',
                margin: 0,
                lineHeight: '1.4'
              }}>
                Vytvoř lekci na vlastní téma
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* Topic input modal */}
      {showTopicModal && (
        <div
          onClick={closeTopicModal}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', padding: '32px', borderRadius: '12px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)', minWidth: '360px', maxWidth: '480px'
            }}
          >
            <h3 style={{ margin: '0 0 12px 0', color: '#1f2937' }}>Nová dynamická lekce</h3>
            <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
              Napiš téma a my ti vygenerujeme dialog s otázkami. Gramatika se zaměří na oblasti, ve kterých máš mezery.
            </p>
            <input
              type="text"
              autoFocus
              placeholder="např. Plánování víkendu"
              value={topicInput}
              onChange={(e) => setTopicInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !generating) handleGenerate();
                if (e.key === 'Escape' && !generating) closeTopicModal();
              }}
              disabled={generating}
              maxLength={50}
              style={{
                width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb',
                borderRadius: '8px', fontSize: '16px', marginBottom: '4px', boxSizing: 'border-box'
              }}
            />
            <div style={{
              textAlign: 'right', fontSize: '12px', color: '#9ca3af', marginBottom: '12px'
            }}>
              {topicInput.length}/50
            </div>
            {generateError && (
              <div style={{
                color: '#b91c1c', fontSize: '13px', marginBottom: '12px',
                backgroundColor: '#fef2f2', border: '1px solid #fecaca', padding: '8px 12px', borderRadius: '6px'
              }}>
                {generateError}
              </div>
            )}
            {generating && (
              <div style={{
                color: '#1d4ed8', fontSize: '13px', marginBottom: '12px',
                backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', padding: '8px 12px', borderRadius: '6px'
              }}>
                Generuji lekci a slovíčka… může to trvat ~20–60 s.
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={closeTopicModal}
                disabled={generating}
                style={{
                  padding: '10px 20px', backgroundColor: '#f3f4f6', color: '#374151',
                  border: 'none', borderRadius: '8px',
                  cursor: generating ? 'not-allowed' : 'pointer', fontWeight: '500',
                  opacity: generating ? 0.6 : 1
                }}
              >
                Zrušit
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                style={{
                  padding: '10px 20px', backgroundColor: '#1d4ed8', color: 'white',
                  border: 'none', borderRadius: '8px',
                  cursor: generating ? 'not-allowed' : 'pointer', fontWeight: '600',
                  opacity: generating ? 0.6 : 1
                }}
              >
                {generating ? 'Generuji…' : 'Vygenerovat'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div
          onClick={() => deletingId == null && setConfirmDelete(null)}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', padding: '32px', borderRadius: '12px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)', minWidth: '320px', maxWidth: '440px'
            }}
          >
            <h3 style={{ margin: '0 0 12px 0', color: '#1f2937' }}>Smazat lekci?</h3>
            <p style={{ margin: '0 0 20px 0', color: '#6b7280', fontSize: '14px' }}>
              Opravdu chceš smazat dynamickou lekci „<strong>{confirmDelete.topic}</strong>"? Tato akce je nevratná.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={deletingId != null}
                style={{
                  padding: '10px 20px', backgroundColor: '#f3f4f6', color: '#374151',
                  border: 'none', borderRadius: '8px',
                  cursor: deletingId != null ? 'not-allowed' : 'pointer', fontWeight: '500'
                }}
              >
                Zrušit
              </button>
              <button
                onClick={handleDeleteDynamic}
                disabled={deletingId != null}
                style={{
                  padding: '10px 20px', backgroundColor: '#dc2626', color: 'white',
                  border: 'none', borderRadius: '8px',
                  cursor: deletingId != null ? 'not-allowed' : 'pointer', fontWeight: '600'
                }}
              >
                {deletingId != null ? 'Mažu…' : 'Smazat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonMenu;
